import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "../../../firebase";
import { toast } from "../../../hooks/useToast";
import { fmtMoeda } from "../../../utils/format";
import { CommerceDialog } from "../../commerce/CommerceDialog";
import { AlertasMercadoPago } from "../AlertasMercadoPago";
import { gatewayPorId } from "./gateways";

/* Pagamentos online para conferir (PagBank, InfinitePay): o servidor cria em
   users/{uid}/pagamentos_alertas quando algo não confere (valor, conta,
   transação repetida…) ou quando houve estorno depois de pago. O dono lê e
   marca como conferido — as regras só deixam mudar resolvidoEm. Os do Mercado
   Pago continuam em mercadopago_alertas (AlertasMercadoPago). */

const MOTIVOS = {
  valor: "O valor do pagamento não confere com o da cobrança.",
  valor_pago_menor: "O valor pago é menor que o da cobrança.",
  moeda: "O pagamento não está em reais.",
  conta_recebedora: "O pagamento não é da conta conectada a esta cobrança.",
  transacao_de_outra_cobranca: "Esta transação já confirmou outra cobrança.",
  pagamento_duplicado: "O pedido recebeu um segundo pagamento. Confira e devolva o excedente pelo provedor.",
  pedido_inexistente: "O pagamento foi confirmado, mas o pedido não existe mais no Print Control.",
  acima_do_saldo: "O pedido já estava pago (por outro meio ou outro link) quando este pagamento foi confirmado, então ele não entrou de novo no pedido nem no caixa. Se foi o mesmo pagamento que você registrou à mão, está tudo certo. Se o cliente pagou duas vezes, devolva o excedente pelo provedor.",
  estornado_apos_pagamento: "O provedor informou estorno ou contestação depois do pagamento. Confira antes de enviar o produto.",
};
export const textoAlertaPagamento = a => (a?.motivo || "").split(",").map(m => MOTIVOS[m]).filter(Boolean).join(" ")
  || "Os dados deste pagamento não conferem com a cobrança, por isso ele não foi marcado como pago.";

export function useAlertasPagamentos(uid) {
  const [alertas, setAlertas] = useState([]);
  useEffect(() => {
    if (!uid) { setAlertas([]); return undefined; }
    return onSnapshot(query(collection(db, "users", uid, "pagamentos_alertas"), where("resolvidoEm", "==", null)),
      snap => setAlertas(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.criadoEm || 0) - (a.criadoEm || 0))),
      () => setAlertas([]));
  }, [uid]);
  return alertas;
}

function ListaAlertasPagamentos({ uid, alertas }) {
  const [salvando, setSalvando] = useState(null);
  if (!alertas.length) return null;
  const conferido = async a => {
    setSalvando(a.id);
    try { await updateDoc(doc(db, "users", uid, "pagamentos_alertas", a.id), { resolvidoEm: serverTimestamp() }); }
    catch { toast("Não foi possível marcar como conferido. Tente de novo.", "error"); }
    finally { setSalvando(null); }
  };
  return (
    <ul className="pgw-lista">
      {alertas.map(a => (
        <li key={a.id} className="pgw-item" style={{ "--gw-cor": gatewayPorId(a.provedor)?.cor }}>
          <div className="pgw-logo">{gatewayPorId(a.provedor)?.sigla || "!"}</div>
          <div className="pgw-texto">
            <div className="pgw-titulo">
              <span className="pgw-nome">{a.tipo === "estornado" ? "Pagamento estornado" : "Pagamento para conferir"}</span>
              <span className="pgw-status alerta"><i />{fmtMoeda(a.valor)}</span>
            </div>
            <div className="pgw-sub">{textoAlertaPagamento(a)}</div>
          </div>
          <div className="pgw-acoes">
            <button type="button" className="pgw-btn" onClick={() => conferido(a)} disabled={salvando === a.id}>
              {salvando === a.id ? <span className="pgw-spin" /> : "Conferido"}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function JanelaAlertas({ uid, alertasPg, comMercadoPago, onClose }) {
  return (
    <CommerceDialog className="pgw-dialogo" title="Pagamentos para conferir" subtitle="Não foram marcados como pagos automaticamente. Confira no provedor antes de liberar o pedido." onClose={onClose}
      footer={<button type="button" className="pgw-btn" onClick={onClose}>Fechar</button>}>
      <div className="pgw-etapa">
        {comMercadoPago && <AlertasMercadoPago uid={uid} />}
        <ListaAlertasPagamentos uid={uid} alertas={alertasPg} />
      </div>
    </CommerceDialog>
  );
}
